// The world clocks: every listener hears the same bar at the same moment (SPEC §1).
//
// There are two streams with the same song model and different tempos: the main stream at
// 125 BPM and its sister at 72 BPM (slow jams, double-time trap). `clockFor(bpm)` gives a
// stream's clock; only tempo-independent numbers (ticks, bars, days) are plain exports.
//
// 125 BPM 4/4 → a beat is 480 ms, a bar 1 920 ms, a tick (960 PPQN) 0.5 ms; a UTC day is 45 000
// bars = 2 647 slots × 17 + a 1-bar dawn interlude. 72 BPM → a bar is 10 000/3 ms (3.333 s), a
// day 25 920 bars = 1 524 slots × 17 + a 12-bar dawn interlude. A bar is always 240 000 / bpm ms,
// so a day is 360 × bpm bars, and samples per bar (sr × 240 / bpm) are integers at 44.1/48 kHz
// for both tempos (84 672 / 92 160 and 147 000 / 160 000).
//
// A contribution is a song of up to three 4-bar patterns arranged into 4 sections = 16 bars; a
// slot is the song + 1 break bar = 17 bars. Everything here is integer arithmetic on
// milliseconds, ticks and bars (products stay far below 2^53), using floor division so negative
// times before the epoch behave. Bar starts at 72 BPM fall on thirds of a millisecond: they are
// computed in one division from integers (never accumulated). `engine/src/world.rs` mirrors
// `barContent`; the engine converts bars to samples itself.

/** The two streams, by tempo. */
export const STREAMS = [125, 72] as const;
export type Bpm = (typeof STREAMS)[number];
export const MAIN_BPM: Bpm = 125;
export const SLOW_BPM: Bpm = 72;
export function isBpm(x: unknown): x is Bpm {
  return x === 125 || x === 72;
}

export const PPQN = 960;
export const BEATS_PER_BAR = 4;
export const TICKS_PER_BEAT = PPQN;
export const TICKS_PER_BAR = PPQN * BEATS_PER_BAR; // 3 840

/** Bars of one pattern (A–C). */
export const LOOP_BARS = 4;
export const LOOP_TICKS = LOOP_BARS * TICKS_PER_BAR; // 15 360
/** A song has up to 3 patterns (intro / main / outro …), arranged into 4 sections of 4 bars. */
export const MAX_PATTERNS = 3;
export const SECTIONS = 4;
export const SONG_BARS = LOOP_BARS * SECTIONS; // 16
export const SONG_TICKS = SONG_BARS * TICKS_PER_BAR; // 61 440
export const SLOT_BARS = SONG_BARS + 1; // 17
export const MS_PER_DAY = 86_400_000;

/** Floor division and modulo that work for negative numbers. */
export function floorDiv(a: number, b: number): number {
  return Math.floor(a / b);
}
export function mod(a: number, b: number): number {
  return ((a % b) + b) % b;
}

/** Parse `SONG_EPOCH` (an ISO date "YYYY-MM-DD", UTC midnight) to epoch milliseconds. */
export function parseEpoch(isoDate: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!m) throw new Error(`SONG_EPOCH must be YYYY-MM-DD, got "${isoDate}"`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Default epoch (UTC midnight of the launch day); configurable via SONG_EPOCH. */
export const DEFAULT_EPOCH_DATE = "2026-10-01";
export const DEFAULT_EPOCH_MS = parseEpoch(DEFAULT_EPOCH_DATE);

export function dayStartMs(day: number, epochMs: number): number {
  return epochMs + day * MS_PER_DAY;
}

/** Day index (since the epoch) of time `ms`. Day 0 is the launch day. */
export function dayAt(ms: number, epochMs: number): number {
  return floorDiv(Math.floor(ms) - epochMs, MS_PER_DAY);
}

/** "YYYY-MM-DD" (UTC) of a day index. */
export function dayToDate(day: number, epochMs: number): string {
  return new Date(dayStartMs(day, epochMs)).toISOString().slice(0, 10);
}

/** Day index of a "YYYY-MM-DD" (UTC) date. */
export function dateToDay(isoDate: string, epochMs: number): number {
  return floorDiv(parseEpoch(isoDate) - epochMs, MS_PER_DAY);
}

/** Milliseconds until the next day starts (after `ms`). */
export function msUntilNextDay(ms: number, epochMs: number): number {
  return dayStartMs(dayAt(ms, epochMs) + 1, epochMs) - ms;
}

/** Freeze time of a day: 60 minutes before it starts (SPEC §2). */
export const FREEZE_LEAD_MS = 60 * 60 * 1000;
export function freezeAtMs(day: number, epochMs: number): number {
  return dayStartMs(day, epochMs) - FREEZE_LEAD_MS;
}

export type BarContent =
  | { kind: "loop"; day: number; slot: number; loopBar: number; section: number }
  | { kind: "break"; day: number; slot: number }
  | { kind: "dawn"; day: number; dawnBar: number };

export interface SongPosition {
  bar: number;
  day: number;
  /** Bar within the day, 0 … BARS_PER_DAY − 1. */
  barInDay: number;
  /** Slot of the day, or null during the dawn interlude. */
  slot: number | null;
  /** Bar within the slot, 0 … 16 (16 = break), or null during the dawn interlude. */
  barInSlot: number | null;
  /** Tick within the current bar, 0 … 3 839. */
  tickInBar: number;
  /** Milliseconds into the current bar, 0 ≤ x < MS_PER_BAR (fractional). */
  msInBar: number;
  content: BarContent;
}

/** One stream's clock. */
export interface Clock {
  readonly bpm: Bpm;
  readonly MS_PER_BEAT: number;
  /** 1 920 at 125 BPM; 3 333.33… (10 000 / 3) at 72 BPM. */
  readonly MS_PER_BAR: number;
  readonly TICKS_PER_MS: number;
  readonly MS_PER_TICK: number;
  readonly SONG_MS: number;
  readonly SLOT_MS: number;
  readonly BARS_PER_DAY: number;
  readonly SLOTS_PER_DAY: number;
  /** First bar (in the day) of the dawn interlude, and its length. */
  readonly DAWN_START_IN_DAY: number;
  readonly DAWN_BARS: number;
  /** World bar containing time `ms` (floor). */
  barAt(ms: number, epochMs: number): number;
  /** Start of bar `bar` in epoch ms (a third of a ms at 72 BPM is kept as a fraction). */
  barStartMs(bar: number, epochMs: number): number;
  /** World tick (since the epoch) at `ms`. Sub-tick precision is floored. */
  tickAt(ms: number, epochMs: number): number;
  /** Time of world tick `tick` in epoch ms. */
  tickToMs(tick: number, epochMs: number): number;
  dayOfBar(bar: number): number;
  dayStartBar(day: number): number;
  /** What plays in world bar `bar` (mirrors `engine::world::bar_content`). */
  barContent(bar: number): BarContent;
  /** First world bar of slot `slot` of day `day`. */
  slotStartBar(day: number, slot: number): number;
  /** Start time (epoch ms) of a slot: when its song's first bar is heard. */
  slotStartMs(day: number, slot: number, epochMs: number): number;
  /** Global slot index (unique across days), used as the break-variation key. */
  globalSlot(day: number, slot: number): number;
  /** Full position of world time `ms` (may be fractional). */
  positionAt(ms: number, epochMs: number): SongPosition;
  /** The time a slot's song plays, as `{ startMs, endMs }` (end = after the break bar). */
  slotWindow(day: number, slot: number, epochMs: number): { startMs: number; endMs: number };
  /** Milliseconds of `ticks`. */
  ticksToMs(ticks: number): number;
  msToTicks(ms: number): number;
  /** Samples per bar at a sample rate: exact integers at 44.1/48 kHz. */
  samplesPerBar(sampleRate: number): number;
  /** Sample offset of `bars` whole bars, floored like the engine's `bar_offset_samples`. */
  barOffsetSamples(bars: number, sampleRate: number): number;
  /** Sample offset of a tick within a bar, rounded like the engine's `tick_offset_samples`. */
  tickOffsetSamples(tick: number, sampleRate: number): number;
}

/** Milliseconds per bar × bpm. */
const BAR_MS_BPM = 240_000;

function makeClock(bpm: Bpm): Clock {
  const MS_PER_BAR = BAR_MS_BPM / bpm;
  const BARS_PER_DAY = 360 * bpm;
  const SLOTS_PER_DAY = Math.floor((BARS_PER_DAY - 1) / SLOT_BARS);
  const DAWN_START_IN_DAY = SLOTS_PER_DAY * SLOT_BARS;
  /** Ticks per ms = bpm × 16 / 1000 (2 at 125 BPM, 1.152 at 72 BPM). */
  const tickNum = bpm * 16;
  const barStartMs = (bar: number, epochMs: number) => epochMs + (bar * BAR_MS_BPM) / bpm;
  const barContent = (bar: number): BarContent => {
    const day = floorDiv(bar, BARS_PER_DAY);
    const r = mod(bar, BARS_PER_DAY);
    if (r >= DAWN_START_IN_DAY) return { kind: "dawn", day, dawnBar: r - DAWN_START_IN_DAY };
    const slot = Math.floor(r / SLOT_BARS);
    const k = r % SLOT_BARS;
    if (k < SONG_BARS) return { kind: "loop", day, slot, loopBar: k, section: Math.floor(k / LOOP_BARS) };
    return { kind: "break", day, slot };
  };
  const slotStartBar = (day: number, slot: number) => day * BARS_PER_DAY + slot * SLOT_BARS;
  const slotStartMs = (day: number, slot: number, epochMs: number) => barStartMs(slotStartBar(day, slot), epochMs);
  return {
    bpm,
    MS_PER_BEAT: MS_PER_BAR / BEATS_PER_BAR,
    MS_PER_BAR,
    TICKS_PER_MS: tickNum / 1000,
    MS_PER_TICK: 1000 / tickNum,
    SONG_MS: (SONG_BARS * BAR_MS_BPM) / bpm,
    SLOT_MS: (SLOT_BARS * BAR_MS_BPM) / bpm,
    BARS_PER_DAY,
    SLOTS_PER_DAY,
    DAWN_START_IN_DAY,
    DAWN_BARS: BARS_PER_DAY - DAWN_START_IN_DAY,
    barAt: (ms, epochMs) => floorDiv((Math.floor(ms) - epochMs) * bpm, BAR_MS_BPM),
    barStartMs,
    tickAt: (ms, epochMs) => Math.floor(((ms - epochMs) * tickNum) / 1000),
    tickToMs: (tick, epochMs) => epochMs + (tick * 1000) / tickNum,
    dayOfBar: (bar) => floorDiv(bar, BARS_PER_DAY),
    dayStartBar: (day) => day * BARS_PER_DAY,
    barContent,
    slotStartBar,
    slotStartMs,
    globalSlot: (day, slot) => day * SLOTS_PER_DAY + slot,
    positionAt(ms, epochMs) {
      const rel = ms - epochMs;
      const bar = floorDiv(rel * bpm, BAR_MS_BPM);
      const msInBar = Math.max(0, rel - (bar * BAR_MS_BPM) / bpm);
      const day = floorDiv(bar, BARS_PER_DAY);
      const barInDay = bar - day * BARS_PER_DAY;
      const dawn = barInDay >= DAWN_START_IN_DAY;
      return {
        bar,
        day,
        barInDay,
        slot: dawn ? null : Math.floor(barInDay / SLOT_BARS),
        barInSlot: dawn ? null : barInDay % SLOT_BARS,
        tickInBar: Math.min(TICKS_PER_BAR - 1, Math.floor((msInBar * tickNum) / 1000)),
        msInBar,
        content: barContent(bar),
      };
    },
    slotWindow(day, slot, epochMs) {
      const startMs = slotStartMs(day, slot, epochMs);
      return { startMs, endMs: barStartMs(slotStartBar(day, slot) + SLOT_BARS, epochMs) };
    },
    ticksToMs: (ticks) => (ticks * 1000) / tickNum,
    msToTicks: (ms) => (ms * tickNum) / 1000,
    samplesPerBar: (sr) => (sr * 240) / bpm,
    barOffsetSamples: (bars, sr) => Math.floor((bars * sr * 240) / bpm),
    tickOffsetSamples: (tick, sr) => Math.floor((tick * sr + 8 * bpm) / (16 * bpm)),
  };
}

const CLOCKS: Record<Bpm, Clock> = { 125: makeClock(125), 72: makeClock(72) };

/** The clock of the stream at `bpm`. */
export function clockFor(bpm: Bpm): Clock {
  return CLOCKS[bpm];
}
